import { toastify } from '../helpers/toastify.js';
import { log } from '../utils/log.js';

/**
 * Error codes and their user-friendly messages (French)
 */
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Erreur de connexion. Veuillez vérifier votre connexion internet.',
  TIMEOUT_ERROR: 'La requête a expiré. Veuillez réessayer.',
  AUTH_ERROR: 'Session expirée. Veuillez vous reconnecter.',
  PRODUCT_NOT_FOUND: 'Produit non trouvé.',
  CLIENT_NOT_FOUND: 'Client non trouvé.',
  DOSSIER_NOT_FOUND: 'Dossier non trouvé.',
  DOSSIER_CREATE_ERROR: 'Erreur lors de la création du dossier.',
  DOSSIER_UPDATE_ERROR: 'Erreur lors de la mise à jour du dossier.',
  VALIDATION_REQUIRED: "Veuillez d'abord valider un client.",
  INVALID_RESPONSE: 'Réponse du serveur invalide.',
  EMPTY_RESPONSE: 'Le serveur a retourné une réponse vide.',
  SCAN_ERROR: 'Erreur lors du scan. Veuillez réessayer.',
  CAMERA_PERMISSION_DENIED:
    "Permission d'accès à la caméra refusée. Veuillez autoriser l'accès.",
  CAMERA_NOT_FOUND: 'Aucune caméra trouvée.',
  UNKNOWN_ERROR: 'Une erreur inattendue est survenue.'
};

/**
 * Maps error types to user-friendly messages
 * @param {Error} error - The error object
 * @returns {string} User-friendly error message
 */
function getErrorMessage(error) {
  const message = error.message?.toLowerCase() || '';

  if (error.name === 'AbortError' || message.includes('timeout') || message.includes('expiré')) {
    return ERROR_MESSAGES.TIMEOUT_ERROR;
  }

  if (error.name === 'TypeError' && message.includes('fetch')) {
    return ERROR_MESSAGES.NETWORK_ERROR;
  }

  if (message.includes('network') || message.includes('connexion')) {
    return ERROR_MESSAGES.NETWORK_ERROR;
  }

  if (message.includes('401') || message.includes('unauthorized') || message.includes('token')) {
    return ERROR_MESSAGES.AUTH_ERROR;
  }

  if (message.includes('produit non trouvé') || message.includes('product not found')) {
    return ERROR_MESSAGES.PRODUCT_NOT_FOUND;
  }

  if (message.includes('client non trouvé') || message.includes('client not found')) {
    return ERROR_MESSAGES.CLIENT_NOT_FOUND;
  }

  if (message.includes('dossier non trouvé') || message.includes('dossier not found')) {
    return ERROR_MESSAGES.DOSSIER_NOT_FOUND;
  }

  if (message.includes('empty response') || message.includes('réponse vide')) {
    return ERROR_MESSAGES.EMPTY_RESPONSE;
  }

  if (message.includes('invalid json') || message.includes('json')) {
    return ERROR_MESSAGES.INVALID_RESPONSE;
  }

  // Return the error message if it's already in French and user-friendly
  if (error.message && !message.includes('error') && error.message.length < 100) {
    return error.message;
  }

  return ERROR_MESSAGES.UNKNOWN_ERROR;
}

/**
 * Handles API errors with logging and user feedback
 * @param {Error} error - The error object
 * @param {string} context - Context description for logging
 * @param {object} options - Additional options
 * @param {boolean} options.showToast - Whether to show toast notification (default: true)
 * @param {string} options.customMessage - Custom message to show instead of default
 */
export function handleApiError(error, context = 'API', options = {}) {
  const { showToast = true, customMessage = null } = options;

  // Log error with context
  log.error(`[${context}] Error:`, error);

  // Get user-friendly message
  const userMessage = customMessage || getErrorMessage(error);

  // Show toast notification
  if (showToast) {
    toastify(userMessage, { variant: 'danger' });
  }

  return userMessage;
}

/**
 * Handles scan-related errors
 * @param {Error} error - The error object
 */
export function handleScanError(error) {
  log.error('[Scanner] Error:', error);

  const message = error.message?.toLowerCase() || '';

  if (message.includes('no barcode') || message.includes('not detected')) {
    // Silent failure for no barcode - this is expected during continuous scanning
    return;
  }

  toastify(ERROR_MESSAGES.SCAN_ERROR, { variant: 'danger' });
}

/**
 * Handles camera permission/access errors
 * @param {Error} error - The error object
 * @returns {string} Error message for display
 */
export function handleCameraError(error) {
  log.error('[Camera] Error:', error);

  if (error.name === 'NotAllowedError') {
    return ERROR_MESSAGES.CAMERA_PERMISSION_DENIED;
  }

  if (error.name === 'NotFoundError') {
    return ERROR_MESSAGES.CAMERA_NOT_FOUND;
  }

  return error.message || ERROR_MESSAGES.UNKNOWN_ERROR;
}

/**
 * Wraps an async function with error handling
 * @param {Function} fn - Async function to wrap
 * @param {string} context - Context description for logging
 * @param {object} options - Error handling options
 * @returns {Function} Wrapped function
 */
export function withErrorHandling(fn, context, options = {}) {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      handleApiError(error, context, options);
      throw error; // Re-throw if caller needs to handle it
    }
  };
}

/**
 * Safe wrapper that catches errors and returns null
 * @param {Function} fn - Async function to wrap
 * @param {string} context - Context description for logging
 * @returns {Function} Wrapped function that returns null on error
 */
export function safeAsync(fn, context) {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      handleApiError(error, context);
      return null;
    }
  };
}
